import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  ChangeDetectorRef,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  ExtractHttpErrorResponseCodeAndMessage,
  IHttpErrorResponseCodeAndMessage,
  IHttpSingleDataResponse,
  IIrembogovBasicLabelKeyPair,
  IUiAlertContent,
  ToastService,
  TUiAlertTypes,
} from '@irembo-andela/irembogov3-common';
import {
  IError,
  JsonEditorComponent,
  JsonEditorOptions,
} from '@maaxgr/ang-jsoneditor';
import { IServiceChangeRequest } from '../../../../core/models/service-change-request.model';
import { IServiceTemplate } from '../../../../core/models/service-template/service-template.model';
import { ServiceChangeRequestService } from '../../../../core/services/service-change-request.service';
import { ServiceTemplateService } from '../../../../core/services/service-template.service';
import { finalize } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { IWorkflowTemplate } from '../../../../core/models/workflow-templates.model';
import {
  IFieldError,
  IServiceConfigStep,
} from '../../../../core/models/service-change-request-config-steps-forms.model';
import { ExtractHttpErrorFieldErrorsUtil } from '../../../../core/utils/http-field-error-extractor.utils';
import { EServiceChangeRequestSection } from '../../../../core/models/service-change-request-sections.enum';
import { EServiceChangeRequestStatus } from './../../../../core/models/service-change-request-status.enum';
import { EServiceChangeRequestToggleHeaders } from './../../../../core/models/service-change-request-toggle-header-section.enum';
import { IServiceChangeRequestWorkflow } from './../../../../core/models/service-change-request-workflow.model';
import { QuillEditorComponent } from 'ngx-quill';
import { isWorkflowBuilderV2Enabled } from '../../irembo-workflow-builder-v2/workflow-builder-v2-feature';

interface IWorkflowNextStep {
  title: string;
  description: string;
  condition?: string | undefined;
}

@Component({
  selector: 'irembogov-configuration-workflow-json-editor-form',
  templateUrl: './configuration-workflow-json-editor-form.component.html',
  styleUrls: ['./configuration-workflow-json-editor-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ConfigurationWorkflowJsonEditorFormComponent
  implements AfterViewInit, OnChanges, OnInit
{
  @Input() changeRequestJsonWorkflowData!: Record<string, unknown>;
  @Input() changeRequest!: IServiceChangeRequest;
  @Input() formFields!: any;
  @Input() isTemplateMode = false;
  @Input() template: IServiceTemplate | null = null;
  @Output() jsonWorkflowFormSubmitted: EventEmitter<
    Record<string, unknown> | undefined
  > = new EventEmitter<Record<string, unknown> | undefined>();

  @ViewChild('errorMessageElement') errorMessageElement!: ElementRef;
  @ViewChild(JsonEditorComponent, { static: false })
  jsonEditor!: JsonEditorComponent;
  isCodeView = false;

  autoSaveInProgress = false;
  lastAutoSaved: Date | null = null;
  autoSaveTimeout: any = null;

  showConfigWorkflowErrorMessage = false;
  jsonEditorValidationErrors: ValidationErrors[] = [];
  EServiceChangeRequestSection = EServiceChangeRequestSection;
  EServiceChangeRequestStatus = EServiceChangeRequestStatus;
  EServiceChangeRequestToggleHeaders = EServiceChangeRequestToggleHeaders;
  // ── opt-in v2 visual workflow builder (additive; existing logic untouched) ──
  workflowBuilderV2Enabled = isWorkflowBuilderV2Enabled();
  useWorkflowBuilderV2 = false;
  selectedOption: EServiceChangeRequestToggleHeaders =
    EServiceChangeRequestToggleHeaders.WIZARD;
  /** Remember the chosen editor tab + v2 toggle across refreshes (like the form builder). */
  private readonly WF_BUILDER_PREFS_KEY = 'irembo-workflow-builder-prefs';
  changeRequestStatus!: string;

  headerOptions: IIrembogovBasicLabelKeyPair<EServiceChangeRequestToggleHeaders>[] =
    [
      { label: 'Wizard', key: EServiceChangeRequestToggleHeaders.WIZARD },
      {
        label: 'Code Editor',
        key: EServiceChangeRequestToggleHeaders.CODE_EDITOR,
      },
      {
        label: 'Visual Editor',
        key: EServiceChangeRequestToggleHeaders.PREVIEW,
      },
    ];

  workflowSteps: IServiceConfigStep[] = [
    {
      icon: 'fa-diagram-project fas',
      label: 'Workflow Config',
      subLabel: 'Workflow',
      key: 'config',
    },
    {
      icon: 'fa-clock fas',
      label: 'SLA',
      subLabel: 'SLA of status changes',
      key: 'sla',
    },
    {
      icon: 'fa-comment fas',
      label: 'Notification',
      subLabel: 'Inform users of status changes',
      key: 'notification',
    },
    {
      icon: 'fa-building fas',
      label: 'Office Assignment',
      subLabel: 'Define offices to process the application',
      key: 'office-assignments',
    },
    {
      icon: 'fa-gears fas',
      label: 'Integrations',
      subLabel: 'Define 3rd party integrations required with your service',
      key: 'integrations',
    },
    {
      icon: 'fa-file fas',
      label: 'Documents',
      subLabel: 'Create certificate using template bulder',
      key: 'documents',
    },
    {
      icon: 'fa-bell fas',
      label: 'Reminders',
      subLabel: 'Set a reminder for the applicant',
      key: 'reminders',
    },
    {
      icon: 'fa-shield-halved fas',
      label: 'Validation Rules',
      subLabel: 'Configure application constraints and rules',
      key: 'restrictions',
    },
    {
      icon: 'fa-receipt fas',
      label: 'Applicant Name Receipt Configuration',
      subLabel: 'Map form fields to the receipt applicant name',
      key: 'receipt-name',
    },
  ];

  currentConfigStep = 0;

  public editorOptions: JsonEditorOptions;
  initialData: Record<string, unknown> = this.changeRequestJsonWorkflowData;

  formAlertContent: IUiAlertContent = {
    title: '',
    message: '',
    type: 'warning',
  };
  isLoading = false;
  nextStepsForm!: FormGroup;
  hide = false;
  backgroundColor = '#ffffff';

  @ViewChild(QuillEditorComponent, { static: true })
  quillEditor!: QuillEditorComponent;

  slaForm = this.fb.group({
    expectedProcessingTime: [0, [Validators.required, Validators.min(1)]],
    expectedProcessingMeasurementUnit: ['DAY', [Validators.required]],
  });

  processingMeasurementUnits = [
    { label: 'Days', value: 'DAY' },
    { label: 'Hours', value: 'HOUR' },
    { label: 'Minutes', value: 'MINUTE' },
  ];

  constructor(
    private toastService: ToastService,
    private serviceChangeRequestService: ServiceChangeRequestService,
    private serviceTemplateService: ServiceTemplateService,
    private fb: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.editorOptions = new JsonEditorOptions();
    this.editorOptions.modes = ['code'];

    this.editorOptions.onValidate = (json: unknown) =>
      this.checkJsonFormDataIsNotEmpty(json as Record<string, unknown>);
    this.createNextStepsForm([]);
  }

  ngOnInit() {
    this.restoreBuilderPrefs();
    this.nextStepsForm.valueChanges.subscribe(data => {
      this.changeRequestJsonWorkflowData['nextSteps'] = data.nextSteps;
      if (this.changeRequestJsonWorkflowData) {
        this.initialData = this.changeRequestJsonWorkflowData;
        this.jsonEditor.update(this.changeRequestJsonWorkflowData as any);
      }
    });

    this.slaForm.valueChanges.subscribe(sla => {
      this.changeRequestJsonWorkflowData['expectedProcessingTime'] =
        sla.expectedProcessingTime;
      this.changeRequestJsonWorkflowData['expectedProcessingMeasurementUnit'] =
        sla.expectedProcessingMeasurementUnit || 'DAY';

      if (this.changeRequestJsonWorkflowData) {
        this.initialData = this.changeRequestJsonWorkflowData;
        this.jsonEditor.update(this.changeRequestJsonWorkflowData as any);
      }
    });

    this.setupAutoSave();
  }

  quillConfig = {
    toolbar: [['bold', 'italic', 'underline'], ['link']],
  };

  createNextStepsForm(steps: IWorkflowNextStep[]) {
    this.nextStepsForm = this.fb.group({
      nextSteps: this.prepopulateNextStepsFormAray(steps),
    });
  }

  prepopulateNextStepsFormAray(steps: IWorkflowNextStep[]): FormArray<any> {
    const formArray: FormArray = this.fb.array([]);
    if (steps && steps.length > 0) {
      steps.forEach(step => {
        formArray.push(
          this.fb.group({
            title: [step.title, [Validators.required, Validators.minLength(1)]],
            description: [
              step.description,
              [Validators.required, Validators.minLength(1)],
            ],
            condition: [step?.condition, []],
          })
        );
      });
    } else {
      formArray.push(
        this.fb.group({
          title: [null, [Validators.required, Validators.minLength(1)]],
          description: [null, [Validators.required, Validators.minLength(1)]],
        })
      );
    }
    return formArray;
  }

  get nextSteps(): FormArray {
    return this.nextStepsForm.get('nextSteps') as FormArray;
  }

  getSteps(form: any) {
    return form.controls.nextSteps.controls;
  }

  trackStepsByIndex(index: number) {
    return index;
  }

  addNextStep() {
    this.nextSteps.push(
      this.fb.group({
        title: ['', Validators.required],
        description: ['', Validators.required],
        condition: [null],
      })
    );
  }

  removeNextStep(i: number) {
    this.nextSteps.removeAt(i);
  }

  ngAfterViewInit(): void {
    this.jsonEditor.setMode('code');
    this.setupAutoSave();
    this.changeDetectorRef.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['changeRequestJsonWorkflowData']?.currentValue) {
      this.changeRequestJsonWorkflowData =
        changes['changeRequestJsonWorkflowData'].currentValue;
      this.initialData = changes['changeRequestJsonWorkflowData'].currentValue;
      this.slaForm
        .get('expectedProcessingTime')
        ?.setValue(
          Object.keys(this.changeRequestJsonWorkflowData).length > 0
            ? parseInt(
                this.changeRequestJsonWorkflowData[
                  'expectedProcessingTime'
                ] as any
              )
            : 0
        );
      this.slaForm
        .get('expectedProcessingMeasurementUnit')
        ?.setValue(
          Object.keys(this.changeRequestJsonWorkflowData).length > 0
            ? (this.changeRequestJsonWorkflowData[
                'expectedProcessingMeasurementUnit'
              ] as any)
            : 'DAY'
        );
      this.updateNextSteps(
        (this.changeRequestJsonWorkflowData[
          'nextSteps'
        ] as IWorkflowNextStep[]) ?? []
      );

      // When the parent step reloads this component (e.g. navigating to officer form
      // and back), ensure the JSON editor is rehydrated with the latest workflow data
      // instead of defaulting to an empty object.
      this.updateJsonEditor();
    }

    if (changes['changeRequest']?.currentValue) {
      this.changeRequestStatus = this.changeRequest.status;
    }
  }

  async validateJsonEditor(): Promise<void> {
    const editorJson: any = this.jsonEditor.getEditor();
    this.jsonEditorValidationErrors = await editorJson.validate();
    if (this.jsonEditorValidationErrors.length > 0) {
      return;
    }
    this.changeRequestJsonWorkflowData =
      this.jsonEditor.get() as unknown as Record<string, unknown>;
  }

  async onWorkflowSubmit(): Promise<void> {
    this.isLoading = true;
    await this.validateJsonEditor();

    if (this.isTemplateMode) {
      await this.onTemplateWorkflowSubmit();
    } else {
      await this.onChangeRequestWorkflowSubmit();
    }
  }

  async onChangeRequestWorkflowSubmit(): Promise<void> {
    if (
      !this.changeRequest.id ||
      !this.checkIfStepIsValid() ||
      !this.nextStepsForm.valid
    ) {
      this.isLoading = false;
      return;
    }

    this.updateFormDisplayedErrorMessageContent(false);
    this.serviceChangeRequestService
      .updateServiceChangeRequestWorkflowDefinitionAndNextStepsAndProcessingTime(
        this.changeRequest.id,
        this.changeRequestJsonWorkflowData
      )
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res: IHttpSingleDataResponse<Record<string, unknown>>) => {
          this.toastService.show({
            body: res.responseMessage,
            type: 'success',
          });
          this.jsonWorkflowFormSubmitted.emit(
            this.checkIfStepIsValid()
              ? this.changeRequestJsonWorkflowData
              : undefined
          );
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          const errorMessageAndCode: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              `Failed to update Change request workflow definition`
            );

          this.toastService.show({
            body: errorMessageAndCode.message,
            type: 'error',
          });

          const fieldErrors: IFieldError[] =
            ExtractHttpErrorFieldErrorsUtil(error);

          this.updateFormDisplayedErrorMessageContent(
            true,
            'danger',
            'Workflow Definition Not Updated',
            errorMessageAndCode.message,
            fieldErrors
          );
        },
      });
  }

  async onTemplateWorkflowSubmit(): Promise<void> {
    if (
      !this.template?.id ||
      !this.checkIfStepIsValid() ||
      !this.nextStepsForm.valid
    ) {
      this.isLoading = false;
      return;
    }

    this.updateFormDisplayedErrorMessageContent(false);
    this.serviceTemplateService
      .updateTemplateWorkflowDefinition(
        this.template.id,
        this.changeRequestJsonWorkflowData
      )
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res: IHttpSingleDataResponse<Record<string, unknown>>) => {
          this.toastService.show({
            body: 'Template workflow definition updated successfully',
            type: 'success',
          });
          this.jsonWorkflowFormSubmitted.emit(
            this.checkIfStepIsValid()
              ? this.changeRequestJsonWorkflowData
              : undefined
          );
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          const errorMessageAndCode: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              `Failed to update template workflow definition`
            );

          this.toastService.show({
            body: errorMessageAndCode.message,
            type: 'error',
          });

          const fieldErrors: IFieldError[] =
            ExtractHttpErrorFieldErrorsUtil(error);

          this.updateFormDisplayedErrorMessageContent(
            true,
            'danger',
            'Template Workflow Definition Not Updated',
            errorMessageAndCode.message,
            fieldErrors
          );
        },
      });
  }

  updateWorkflowData(selectedOption: EServiceChangeRequestToggleHeaders) {
    if (selectedOption === EServiceChangeRequestToggleHeaders.CODE_EDITOR) {
      //update code editor nextSteps
      this.changeRequestJsonWorkflowData['nextSteps'] =
        this.nextStepsForm.value.nextSteps;
      if (this.changeRequestJsonWorkflowData) {
        this.initialData = this.changeRequestJsonWorkflowData;
        this.jsonEditor.update(this.initialData as any);
      }
    } else {
      // Capture any manual edits made in the code editor before leaving it. When the editor
      // was never mounted (e.g. switching straight between WIZARD and the visual builder)
      // there is nothing to capture — bail out instead of dereferencing an undefined editor.
      if (!this.jsonEditor) return;
      //update next steps with workflow changes
      const workflowChanges = this.jsonEditor.get() as unknown as Record<
        string,
        unknown
      >;
      this.changeRequestJsonWorkflowData = workflowChanges;
      this.updateNextSteps(workflowChanges['nextSteps'] as IWorkflowNextStep[]);
      this.slaForm
        .get('expectedProcessingTime')
        ?.setValue(workflowChanges['expectedProcessingTime'] as number);
      this.slaForm
        .get('expectedProcessingMeasurementUnit')
        ?.setValue(
          workflowChanges['expectedProcessingMeasurementUnit'] as string
        );
    }
  }

  updateNextSteps(steps: IWorkflowNextStep[]) {
    this.nextSteps.clear();
    steps.forEach(step => {
      this.nextSteps.push(this.fb.group(step));
    });
    this.nextStepsForm.controls['nextSteps'].markAllAsTouched();
    this.nextStepsForm.controls['nextSteps'].updateValueAndValidity();
  }

  updateFormDisplayedErrorMessageContent(
    show: boolean,
    type: TUiAlertTypes = 'warning',
    title = '',
    message = '',
    fieldErrors: IFieldError[] | null = null
  ): void {
    let extraMessage = '';
    this.showConfigWorkflowErrorMessage = show;

    if (fieldErrors) {
      extraMessage = fieldErrors
        .map((field: IFieldError) => `${field.field}: ${field?.errorCode}`)
        .join(`\n`);
    }

    this.formAlertContent = { type, title, message, extraMessage };
    if (show) {
      this.errorMessageElement.nativeElement.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  private checkIfStepIsValid(): boolean {
    if (
      this.jsonEditorValidationErrors.length > 0 ||
      !this.changeRequestJsonWorkflowData ||
      !this.nextStepsForm?.valid ||
      !this.slaForm.valid
    ) {
      this.updateFormDisplayedErrorMessageContent(
        true,
        'danger',
        'Invalid JSON Workflow Entry!',
        'Please ensure you have filled a valid JSON workflow entry.'
      );
      return false;
    }

    return true;
  }

  private checkJsonFormDataIsNotEmpty(json: Record<string, unknown>): IError[] {
    const errors: IError[] = [];
    const workFlowData: IServiceChangeRequestWorkflow = {
      ...(json as unknown as IServiceChangeRequestWorkflow),
    };
    const propertiesArray: string[] = Object.keys(json);
    if (propertiesArray.includes('isTrusted')) {
      propertiesArray.splice(propertiesArray.indexOf('isTrusted'), 1);
    }

    if (propertiesArray.length < 1) {
      errors.push({
        path: [1],
        message: 'JSON Object can not be empty.',
      });
    }

    if (!workFlowData.workflow || !workFlowData.nextSteps) {
      errors.push({
        path: [2],
        message:
          'Invalid workflow!. Workflow, nextSteps, expectedProcessingTime, and expectedProcessingMeasurementUnit must be specified',
      });
    }

    if (
      (workFlowData.workflow && workFlowData.workflow.length === 0) ||
      (workFlowData.nextSteps && workFlowData.nextSteps.length === 0) ||
      workFlowData.expectedProcessingTime === null ||
      workFlowData.expectedProcessingTime === 0 ||
      workFlowData.expectedProcessingMeasurementUnit === null ||
      workFlowData.expectedProcessingMeasurementUnit === ''
    ) {
      errors.push({
        path: [3],
        message:
          "Invalid workflow!. Workflow, nextSteps, expectedProcessingMeasurementUnit can't be empty and expectedProcessingTime must be greater than zero",
      });
    }

    return errors;
  }

  onSelectWorkflowTemplate(template: IWorkflowTemplate): void {
    const templateWorkflowDefinition = this.parseWorkflowTemplateDefinition(
      template?.workflowDefinition
    );
    const expectedProcessingTime = this.normalizedExpectedProcessingTime(
      templateWorkflowDefinition
    );
    const expectedProcessingMeasurementUnit =
      (templateWorkflowDefinition[
        'expectedProcessingMeasurementUnit'
      ] as string) ||
      (this.changeRequestJsonWorkflowData[
        'expectedProcessingMeasurementUnit'
      ] as string) ||
      'DAY';

    this.changeRequestJsonWorkflowData = {
      ...this.changeRequestJsonWorkflowData,
      ...templateWorkflowDefinition,
      workflow: templateWorkflowDefinition['workflow'] ?? [],
      nextSteps: templateWorkflowDefinition['nextSteps'] ?? [],
      expectedProcessingTime,
      expectedProcessingMeasurementUnit,
    };

    this.updateNextSteps(
      (this.changeRequestJsonWorkflowData[
        'nextSteps'
      ] as IWorkflowNextStep[]) ?? []
    );
    this.slaForm
      .get('expectedProcessingTime')
      ?.setValue(expectedProcessingTime);
    this.slaForm
      .get('expectedProcessingMeasurementUnit')
      ?.setValue(expectedProcessingMeasurementUnit);
    this.jsonEditorValidationErrors = [];
    this.updateJsonEditor();
    void this.validateJsonEditor();

    this.toastService.show({
      body: `Workflow template "${template.workflowName}" loaded`,
      type: 'warning',
    });
  }

  private parseWorkflowTemplateDefinition(
    workflowDefinition: IWorkflowTemplate['workflowDefinition'] | string
  ): Record<string, unknown> {
    if (typeof workflowDefinition === 'string') {
      try {
        const parsed = JSON.parse(workflowDefinition);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? (parsed as Record<string, unknown>)
          : {};
      } catch {
        return {};
      }
    }

    return workflowDefinition &&
      typeof workflowDefinition === 'object' &&
      !Array.isArray(workflowDefinition)
      ? (workflowDefinition as Record<string, unknown>)
      : {};
  }

  private normalizedExpectedProcessingTime(
    workflowDefinition: Record<string, unknown>
  ): number {
    const value =
      workflowDefinition['expectedProcessingTime'] ??
      workflowDefinition['expectedProcessingDays'] ??
      this.changeRequestJsonWorkflowData?.['expectedProcessingTime'] ??
      1;
    const parsed = parseInt(value as string, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  onSelectOptionChange(option: EServiceChangeRequestToggleHeaders) {
    if (option !== EServiceChangeRequestToggleHeaders.CODE_EDITOR) {
      // Capture code-editor edits before the visual editor is mounted. The v2 builder loads
      // its input once to avoid echo reloads, so mounting it with stale JSON means later editor
      // corrections are intentionally ignored by the builder.
      this.updateWorkflowData(option);
    }
    this.selectedOption = option;
    this.changeDetectorRef.detectChanges();
    if (option === EServiceChangeRequestToggleHeaders.CODE_EDITOR) {
      // The <json-editor> is (re)created by the *ngIf above and its underlying editor
      // initialises asynchronously. Push the latest workflow — including edits just made in
      // the visual builder — only once the editor is ready; calling update() synchronously
      // here leaves it showing stale data until the user toggles tabs to force a refresh.
      setTimeout(() => {
        if (!this.jsonEditor) return;
        this.jsonEditor.setMode('code');
        this.updateWorkflowData(option);
      });
    }
    this.persistBuilderPrefs();
  }

  toggleWorkflowBuilderVersion(): void {
    this.useWorkflowBuilderV2 = !this.useWorkflowBuilderV2;
    this.persistBuilderPrefs();
  }

  /** Restore the v2 builder toggle only — NOT the active tab (restoring CODE_EDITOR skips the handler that loads the workflow into the editor). */
  private restoreBuilderPrefs(): void {
    try {
      const raw = localStorage.getItem(this.WF_BUILDER_PREFS_KEY);
      if (!raw) return;
      const p = JSON.parse(raw) as {
        useWorkflowBuilderV2?: boolean;
      };
      if (typeof p.useWorkflowBuilderV2 === 'boolean') {
        this.useWorkflowBuilderV2 = p.useWorkflowBuilderV2;
      }
    } catch {
      /* localStorage unavailable / malformed — fall back to defaults */
    }
  }

  private persistBuilderPrefs(): void {
    try {
      localStorage.setItem(
        this.WF_BUILDER_PREFS_KEY,
        JSON.stringify({
          useWorkflowBuilderV2: this.useWorkflowBuilderV2,
        })
      );
    } catch {
      /* localStorage unavailable — ignore */
    }
  }

  OnStepperChange(index: number) {
    this.currentConfigStep = index;
  }

  OnUpdateProcessingDays() {
    this.isLoading = true;
    if (
      !this.changeRequest.id ||
      !this.checkIfStepIsValid() ||
      !this.nextStepsForm.valid ||
      !this.slaForm.valid
    ) {
      this.isLoading = false;
      return;
    }

    this.updateFormDisplayedErrorMessageContent(false);
    const updateObservable =
      this.isTemplateMode && this.template?.id
        ? this.serviceTemplateService.updateTemplateWorkflowDefinition(
            this.template.id,
            this.changeRequestJsonWorkflowData
          )
        : this.serviceChangeRequestService.updateServiceChangeRequestWorkflowDefinitionAndNextStepsAndProcessingTime(
            this.changeRequest.id!,
            this.changeRequestJsonWorkflowData
          );

    updateObservable
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.autoSaveInProgress = false;
        })
      )
      .subscribe({
        next: (res: IHttpSingleDataResponse<Record<string, unknown>>) => {
          this.toastService.show({
            body: res.responseMessage || 'Updated successfully',
            type: 'success',
          });
          this.currentConfigStep++;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          const errorMessageAndCode: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              `Failed to update ${
                this.isTemplateMode ? 'template' : 'change request'
              } workflow definition`
            );

          this.toastService.show({
            body: errorMessageAndCode.message,
            type: 'error',
          });

          const fieldErrors: IFieldError[] =
            ExtractHttpErrorFieldErrorsUtil(error);

          this.updateFormDisplayedErrorMessageContent(
            true,
            'danger',
            'Workflow Definition Not Updated',
            errorMessageAndCode.message,
            fieldErrors
          );
        },
      });
  }

  goToNextStep(): void {
    if (this.currentConfigStep < this.workflowSteps.length - 1) {
      switch (this.currentConfigStep) {
        case 0:
          this.onWorkflowSubmit();
          break;
        case 1:
          this.OnUpdateProcessingDays();
          break;
        case 2:
          this.currentConfigStep++;
          break;
        case 3:
          this.currentConfigStep++;
          break;
        case 4:
          this.currentConfigStep++;
          break;
        case 5:
          this.currentConfigStep++;
          break;
        case 6:
          this.currentConfigStep++;
          break;
        case 7:
          this.currentConfigStep++;
          break;
        case 8:
          this.currentConfigStep++;
          break;
      }
      return;
    }
    if (this.currentConfigStep >= this.workflowSteps.length - 1) {
      // Go to transition step
      this.jsonWorkflowFormSubmitted.emit(this.changeRequestJsonWorkflowData);
    }
  }

  /**
   * Handle workflow update from child components (notifications, office assignment, restrictions, etc.)
   */
  onWorkflowUpdate(updatedWorkflow: unknown) {
    // The state-machine visual editor and notification-configuration both
    // emit the transitions ARRAY directly. Spreading an array into an object
    // (`{...arr}`) produces numeric-keyed clones ("0", "1", ...) that get
    // merged alongside the existing `workflow` field, corrupting the JSON.
    // Other callers (configuration-restrictions) emit a full object delta —
    // those are still spread normally.
    if (Array.isArray(updatedWorkflow)) {
      this.changeRequestJsonWorkflowData = {
        ...this.changeRequestJsonWorkflowData,
        workflow: updatedWorkflow,
      };
    } else {
      this.changeRequestJsonWorkflowData = {
        ...this.changeRequestJsonWorkflowData,
        ...(updatedWorkflow as Record<string, unknown>),
      };
    }
    this.updateJsonEditor();
  }

  private updateJsonEditor() {
    if (this.jsonEditor) {
      this.jsonEditor.update(this.changeRequestJsonWorkflowData as any);
    }
  }

  private setupAutoSave(): void {
    setTimeout(() => {
      if (this.jsonEditor && this.jsonEditor.getEditor()) {
        const editorInstance = this.jsonEditor.getEditor();

        if (editorInstance && editorInstance.options) {
          // Store the original onChange handler if it exists
          const originalOnChange = editorInstance.options.onChange;

          // Replace with our own handler that triggers autosave
          editorInstance.options.onChange = () => {
            // Call original onChange if it exists
            if (originalOnChange) {
              originalOnChange();
            }

            // Debounce the changes to avoid excessive saves
            if (this.autoSaveTimeout) {
              clearTimeout(this.autoSaveTimeout);
            }

            this.autoSaveTimeout = setTimeout(async () => {
              if (
                (this.changeRequest?.id || this.template?.id) &&
                !this.autoSaveInProgress
              ) {
                try {
                  const currentJson = this.jsonEditor.get();

                  const editorJson = this.jsonEditor.getEditor();
                  const validationErrors = await editorJson.validate();

                  if (!validationErrors || validationErrors.length === 0) {
                    this.changeRequestJsonWorkflowData =
                      currentJson as unknown as Record<string, unknown>;
                    this.autoSave();
                  }
                  // else: JSON has validation errors → skip this autosave tick
                } catch {
                  // invalid JSON while the user is typing → skip this autosave tick
                }
              }
            }, 3000); // 3 second debounce
          };
        }
      }
    }, 800); // Give time for editor to initialize
  }

  private autoSave(): void {
    if (
      (!this.changeRequest.id && !this.template?.id) ||
      !this.checkIfStepIsValid() ||
      !this.nextStepsForm.valid
    ) {
      this.isLoading = false;
      return;
    }

    this.autoSaveInProgress = true;

    this.validateJsonEditor().then(() => {
      if (
        this.jsonEditorValidationErrors.length === 0 &&
        (this.changeRequest.id || this.template?.id)
      ) {
        const updateObservable =
          this.isTemplateMode && this.template?.id
            ? this.serviceTemplateService.updateTemplateWorkflowDefinition(
                this.template.id,
                this.changeRequestJsonWorkflowData
              )
            : this.serviceChangeRequestService.updateServiceChangeRequestWorkflowDefinitionAndNextStepsAndProcessingTime(
                this.changeRequest.id!,
                this.changeRequestJsonWorkflowData
              );

        updateObservable
          .pipe(
            finalize(() => {
              this.autoSaveInProgress = false;
            })
          )
          .subscribe({
            next: (res: IHttpSingleDataResponse<Record<string, unknown>>) => {
              this.lastAutoSaved = new Date();
            },
            error: (error: HttpErrorResponse) => {
              if (this.autoSaveTimeout) {
                clearTimeout(this.autoSaveTimeout);
              }

              // Set a timeout to try again after 10 seconds
              this.autoSaveTimeout = setTimeout(() => {
                this.autoSaveInProgress = false;
                this.autoSave();
              }, 10000);

              // Don't show error toasts for autosave failures to avoid disrupting the user
              console.error('Autosave failed:', error);
            },
          });
      } else {
        // If JSON is invalid, reset the autosave flag
        this.autoSaveInProgress = false;
      }
    });
  }
}
